import { styled, Typography, Stack, IconButton } from '@mui/material';
import { CodeBlock } from 'react-code-block';

const codeColors = {
  background: '#151621',
  border: '#383B51',
  button: '#383B51',
  main: '#FFFFFF',
  token: '#FFFFFF',
  function: '#6DA6F8',
  string: '#7CC98A',
};

export const StyledPlayerCardGenerator = styled(Stack)<{
  expanded: boolean;
}>(({ expanded = false, maxHeight = 1500, theme }) => ({
  maxWidth: '660px',
  width: '100%',
  alignSelf: 'center',
  backgroundColor: codeColors.background,
  borderRadius: '24px',
  border: `1px solid ${codeColors.border}`,
  overflow: 'hidden',
  maxHeight: expanded ? `${maxHeight}px` : '755px',
  transition: 'max-height 0.7s ease-in-out',
  [theme.breakpoints.down('md')]: {
    maxHeight: expanded ? `${+maxHeight * 1.5}px` : '620px',
  },
}));

export const StyledPlayerImage = styled('img')({
  width: '100%',
  maxWidth: '200px',
  height: 'auto',
  borderRadius: '14px',
  margin: '0 auto',
});

export const StyledPlayerName = styled(Typography)(({ theme }) => ({
  color: codeColors.main,
  fontFamily: 'ClashDisplay',
  fontSize: '32px',
  letterSpacing: '0.2px',
  lineHeight: '40px',
  fontWeight: 600,
  width: '100%',
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    fontSize: '24px',
    lineHeight: '32px',
  },
}));

export const StyledCardElement = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.4),
  padding: theme.spacing(4, 2.4, 2.4, 2.4),
  height: '100%',
  width: '100%',
  ['&:not(:first-of-type)']: {
    padding: theme.spacing(2.4),
    gap: theme.spacing(0),
    borderTop: `1px solid ${codeColors.border}`,
  },
}));

export const StyledCodeBlockToken = styled(CodeBlock.Token)(() => ({
  fontFamily: 'Satoshi, monospace',
  fontSize: '14px',
  lineHeight: '16.8px',
  fontWeight: 400,
  color: codeColors.token,
  ['&.string']: {
    color: codeColors.string + '!important',
  },
  ['&.function']: {
    color: codeColors.function + '!important',
  },
}));

export const StyledCodeBlockLineContent = styled(CodeBlock.LineContent)(() => ({
  color: codeColors.main,
  backgroundColor: codeColors.background,
  width: '100%',
  wordBreak: 'break-all',
  whiteSpace: 'pre-wrap',
}));

export const StyledButton = styled('button')<{ active: boolean }>(
  ({ active, theme }) => ({
    backgroundColor: active ? codeColors.button : 'transparent',
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 2),
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    fontFamily: 'Satoshi, monospace',
    fontSize: '14px',
    lineHeight: '18.9px',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: codeColors.button,
    },
    '&:disabled': {
      cursor: 'pointer',
    },
  })
);

export const StyledPromptsMenu = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  justifyContent: 'flex-start',
  alignItems: 'center',
}));

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderRadius: '50%',
  padding: '0.5rem',
  transition: 'transform 0.3s',
}));
